import type { Locale, ResumeContent } from '@/types/resume.types'

/**
 * Двуязычный контент резюме (источник истины — design_handoff/reference/data.js).
 * Текст скопирован дословно. Структурированные данные читаются по текущей локали
 * через composable `useResume`, а vue-i18n управляет переключением языка.
 */
export const RESUME: Record<Locale, ResumeContent> = {
  en: {
    nav: { about: 'About', experience: 'Experience', skills: 'Skills', projects: 'Projects', education: 'Education', contact: 'Contact' },
    hero: {
      kicker: 'Frontend Architect · Team Lead',
      name: 'Umar Mamatov',
      roleLines: ['Frontend Architecture', 'Micro-frontends', 'UI Libraries'],
      summary:
        'I lead frontend teams while staying hands-on — actively building features alongside the team, not just reviewing them. I architect scalable frontends, ship micro-frontends, and craft corporate UI libraries. Strong full-stack background (Node.js) and high-load optimization — accelerating delivery, unifying interfaces, and keeping products stable under heavy load.',
      cta: 'Get in touch',
      cta2: 'View experience',
      locationLabel: 'Based in',
      location: 'Tashkent, Uzbekistan',
      availLabel: 'Open to',
      avail: 'Team Lead & Frontend Developer roles',
      chips: ['Vue 3', 'Nuxt 3', 'TypeScript', 'Micro-frontends', 'Web3.js', 'Node.js'],
      status: { a: '8+ yrs experience', b: 'team lead · hands-on', c: 'Tashkent · GMT+5', d: 'open to lead & dev roles' },
    },
    stats: [
      { value: '8+', label: 'Years building for the web' },
      { value: '50+', label: 'Production products shipped' },
      { value: '6', label: 'Engineers led at peak' },
      { value: '3', label: 'Micro frontend architectures' },
    ],
    sectionTitles: {
      experience: 'Experience',
      experienceSub: 'A decade across fintech, gaming, blockchain and high-load SaaS',
      skills: 'Skills',
      skillsSub: "Where I'm strong, and what I'm growing into",
      projects: 'Projects',
      projectsSub: 'Selected products I built or led',
      code: 'Code samples',
      education: 'Education',
      languages: 'Languages',
      contact: "Let's work together",
      contactSub: 'Open to both Team Lead and Frontend Developer offers. Fastest reply via Telegram.',
    },
    experience: [
      {
        company: 'Rahmat Tech', role: 'Team Lead, Frontend Engineering',
        period: 'Jan 2026 — Present', current: true,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Micro-frontends', 'Storybook'],
        points: [
          'Lead the frontend team — architecture, project design and delivery standards.',
          'Stay hands-on as an active contributor — building features and modules alongside the team, not only reviewing them.',
        ],
      },
      {
        company: 'Easy Technologies', role: 'Team Lead, Frontend Engineering',
        period: 'Mar 2024 — Jan 2026', current: false,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Micro-frontends', 'FastAPI'],
        points: [
          'Designed and implemented a scalable frontend architecture with a micro-frontend approach.',
          'Built and maintained a company-wide UI component library (npm package) for design consistency and reuse across all projects.',
          'Drove performance optimization on high-load applications with efficient modular integration.',
          'Developed REST APIs with FastAPI and Node.js for seamless frontend–backend integration.',
        ],
      },
      {
        company: 'Sidus Games + Platinum', role: 'Frontend Engineer',
        period: 'Nov 2022 — Nov 2023', current: false,
        stack: ['JavaScript', 'Vue 3', 'Blockchain', 'web3.js', 'TypeScript', 'PlayCanvas'],
        points: [
          'Built the “Bank” module and a chat for the Organizations module.',
          'Contributed to the PlayCanvas game Asterally.',
        ],
      },
      {
        company: 'Platinum', role: 'Frontend Engineer · Fintech / Crypto startup',
        period: 'Aug 2021 — Nov 2023', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue 3', 'Nuxt.js', 'Express.js', 'Docker', 'MongoDB', 'web3.js', 'TypeScript'],
        points: [
          'Authored Vue components for the Storybook library.',
          'Built the admin panel, the 2048 game, a blockchain bridge and more for the Spaceswap project.',
          'Developed the entire frontend for the CHEESUS token claim & staking.',
        ],
      },
      {
        company: 'Elastoo Products', role: 'Frontend Engineer · Fintech / Crypto startup',
        period: 'Nov 2020 — Aug 2021', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue.js', 'Nuxt.js', 'web3.js'],
        points: ['Frontend development across the product suite.'],
      },
      {
        company: 'Freelance', role: 'Full-Stack Developer',
        period: 'Dec 2017 — Nov 2020', current: false,
        stack: ['JavaScript', 'Vue.js', 'PHP', 'Nuxt.js', 'Yii2', 'MySQL'],
        points: ['Delivered full-stack web projects end to end for a range of clients.'],
      },
    ],
    skills: {
      groups: [
        { label: 'Excellent', level: 4, items: ['JavaScript', 'TypeScript', 'Vue.js', 'Nuxt.js', 'Web3.js', 'Telegram Web App', 'HTML', 'CSS / SCSS', 'Microfrontends'] },
        { label: 'Good', level: 3, items: ['Express.js', 'Node.js (API)', 'GitHub Actions', 'Webpack', 'Vite', 'Docker', 'MongoDB', 'Linux', 'Storybook'] },
        { label: 'Familiar', level: 2, items: ['PlayCanvas', 'Python', 'FastAPI', 'React.js', 'Browser extensions'] },
        { label: 'Experienced', level: 1, items: ['Yii2', 'PHP', 'MySQL'] },
      ],
      levelLabels: { 4: 'Excellent', 3: 'Good', 2: 'Experienced', 1: 'Familiar' },
    },
    projects: [
      { name: 'Mobistore', url: 'mobistore.uz', team: 'Full-stack', tags: ['Nuxt', 'Node.js', 'Express.js', 'PostgreSQL'], desc: 'Full-stack classifieds platform for buying & selling smartphones — listings, search and a seller dashboard. Built both the frontend and the backend.' },
      { name: 'GTS', url: 'globaltravel.space', team: 'Frontend + Backend', tags: ['Vue', 'Node.js'], desc: 'Built both the frontend and backend of the platform.' },
      { name: 'SidusHeroes', url: 'play.sidusheroes.com', team: 'Team of 10', tags: ['Vue 3', 'web3.js', 'PlayCanvas'], desc: 'Sticker chat, full org chat, the bank, and more. Migrated components into a shared npm library.' },
      { name: 'Platinum / Spaceswap', url: 'spaceswap.app', team: 'Team of 5', tags: ['Vue 3', 'Node.js', 'Blockchain'], desc: 'Cross-chain bridge & NFT presale frontend. Full-stack 2048 game and an admin panel for token owners.' },
      { name: 'Vesting', url: 'vesting.cheesus.ai', team: 'Solo frontend', tags: ['Vue 3', 'web3.js'], desc: 'Entire frontend for the CHEESUS token claim & staking flow.' },
      { name: 'ShippedPro', url: 'shippedpro.com', team: 'Solo frontend', tags: ['Vue', 'Nuxt'], desc: 'Developed the entire frontend of the product.' },
      { name: 'Linda', url: 'join-linda.com', team: 'Freelance', tags: ['Vue', 'Chrome ext'], desc: 'Frontend of the platform plus a companion Chrome extension.' },
      { name: 'Onesix', url: 'onesix.tech', team: 'Freelance', tags: ['Vue', 'Nuxt'], desc: 'Frontend of the product.' },
      { name: 'Limecoin', url: '@OfficialLimeCoinBot', team: 'Freelance', tags: ['Telegram WebApp'], desc: 'Telegram web application project.' },
      { name: 'EasyBooking', url: 'easybooking.uz', team: 'Team of 2 FE', tags: ['Yii2', 'PHP', 'Vue'], desc: 'Frontend with a team of two; owned the entire Yii2 (PHP) backend.' },
    ],
    code: [
      { name: 'Contact manager', stack: 'Vue 3', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Mini personal account', stack: 'Nuxt 3', note: 'login: admin · pass: admin', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Ball game', stack: 'Vue 3 + PlayCanvas', links: [['GitHub', '#']] },
    ],
    education: { school: 'Tashkent University of Information Technologies', period: 'Sep 2011 — Jul 2015', degree: "Bachelor's degree with honours", grade: '87 / 100' },
    languages: [
      { name: 'Uzbek', level: 'Native', pct: 100 },
      { name: 'Russian', level: 'Advanced', pct: 85 },
      { name: 'English', level: 'Intermediate', pct: 60 },
    ],
    contact: {
      phone: '+998 97 575 51 51',
      emails: ['boss.mamatov@gmail.com', 'mamatov.umar@mail.ru'],
      telegram: '@Mamatov_Umar',
      telegramUrl: 'https://t.me/Mamatov_Umar',
      linkedin: 'in/umar-mamatov',
      linkedinUrl: 'https://www.linkedin.com/in/umar-mamatov-4a3a27219/',
      labels: { phone: 'Phone', email: 'Email', telegram: 'Telegram', linkedin: 'LinkedIn' },
    },
    footer: 'Designed & built by Umar Mamatov',
  },

  ru: {
    nav: { about: 'О себе', experience: 'Опыт', skills: 'Навыки', projects: 'Проекты', education: 'Образование', contact: 'Контакты' },
    hero: {
      kicker: 'Frontend-архитектор · Тимлид',
      name: 'Умар Маматов',
      roleLines: ['Frontend-архитектура', 'Микрофронтенды', 'UI-библиотеки'],
      summary:
        'Руковожу frontend-командой и при этом остаюсь в коде — активно разрабатываю функционал вместе с командой, а не только ревьюю. Проектирую масштабируемую архитектуру, внедряю микрофронтенды и создаю корпоративные UI-библиотеки. Сильный full-stack бэкграунд (Node.js) и опыт оптимизации высоконагруженных систем — ускоряю разработку, унифицирую интерфейсы и держу продукт стабильным под нагрузкой.',
      cta: 'Связаться',
      cta2: 'Смотреть опыт',
      locationLabel: 'Локация',
      location: 'Ташкент, Узбекистан',
      availLabel: 'Открыт к',
      avail: 'ролям тимлида и frontend-разработчика',
      chips: ['Vue 3', 'Nuxt 3', 'TypeScript', 'Микрофронтенды', 'Web3.js', 'Node.js'],
      status: { a: '8+ лет опыта', b: 'тимлид · в коде', c: 'Ташкент · GMT+5', d: 'открыт к ролям лида и разработчика' },
    },
    stats: [
      { value: '8+', label: 'Лет в веб-разработке' },
      { value: '50+', label: 'Продуктов в продакшене' },
      { value: '6', label: 'Инженеров под руководством' },
      { value: '3', label: 'Микрофронтенды' },
    ],
    sectionTitles: {
      experience: 'Опыт',
      experienceSub: 'Десятилетие в финтехе, геймдеве, блокчейне и высоконагруженном SaaS',
      skills: 'Навыки',
      skillsSub: 'Где я силён и что развиваю',
      projects: 'Проекты',
      projectsSub: 'Избранные продукты, которые я делал или вёл',
      code: 'Примеры кода',
      education: 'Образование',
      languages: 'Языки',
      contact: 'Давайте работать вместе',
      contactSub: 'Открыт к предложениям как тимлида, так и frontend-разработчика. Быстрее всего отвечу в Telegram.',
    },
    experience: [
      {
        company: 'Rahmat Tech', role: 'Тимлид Frontend-инженеров',
        period: 'Янв 2026 — наст. время', current: true,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Микрофронтенды', 'Storybook'],
        points: [
          'Руковожу frontend-командой — архитектура, проектирование и стандарты разработки.',
          'Остаюсь активным разработчиком — пишу фичи и модули вместе с командой, а не только ревьюю.',
        ],
      },
      {
        company: 'Easy Technologies', role: 'Тимлид Frontend-инженеров',
        period: 'Март 2024 — Янв 2026', current: false,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Микрофронтенды', 'FastAPI'],
        points: [
          'Спроектировал и внедрил масштабируемую frontend-архитектуру на основе микрофронтендов.',
          'Создал и поддерживал корпоративную UI-библиотеку компонентов (npm-пакет) для единообразия и переиспользования во всех проектах.',
          'Оптимизировал производительность высоконагруженных приложений с эффективной модульной интеграцией.',
          'Разрабатывал REST API на FastAPI и Node.js для бесшовной интеграции фронта и бэка.',
        ],
      },
      {
        company: 'Sidus Games + Platinum', role: 'Frontend-инженер',
        period: 'Ноя 2022 — Ноя 2023', current: false,
        stack: ['JavaScript', 'Vue 3', 'Blockchain', 'web3.js', 'TypeScript', 'PlayCanvas'],
        points: [
          'Реализовал модуль «Банк» и чат для модуля «Организации».',
          'Участвовал в разработке игры Asterally на PlayCanvas.',
        ],
      },
      {
        company: 'Platinum', role: 'Frontend-инженер · Финтех / Крипто-стартап',
        period: 'Авг 2021 — Ноя 2023', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue 3', 'Nuxt.js', 'Express.js', 'Docker', 'MongoDB', 'web3.js', 'TypeScript'],
        points: [
          'Разрабатывал Vue-компоненты для библиотеки Storybook.',
          'Сделал админ-панель, игру 2048, блокчейн-мост и другие фичи для проекта Spaceswap.',
          'Полностью реализовал фронтенд для клейма и стейкинга токенов CHEESUS.',
        ],
      },
      {
        company: 'Elastoo Products', role: 'Frontend-инженер · Финтех / Крипто-стартап',
        period: 'Ноя 2020 — Авг 2021', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue.js', 'Nuxt.js', 'web3.js'],
        points: ['Frontend-разработка продуктов компании.'],
      },
      {
        company: 'Фриланс', role: 'Full-Stack разработчик',
        period: 'Дек 2017 — Ноя 2020', current: false,
        stack: ['JavaScript', 'Vue.js', 'PHP', 'Nuxt.js', 'Yii2', 'MySQL'],
        points: ['Делал full-stack веб-проекты «под ключ» для разных клиентов.'],
      },
    ],
    skills: {
      groups: [
        { label: 'Отлично', level: 4, items: ['JavaScript', 'TypeScript', 'Vue.js', 'Nuxt.js', 'Web3.js', 'Telegram Web App', 'HTML', 'CSS / SCSS', 'Микрофронтенды'] },
        { label: 'Хорошо', level: 3, items: ['Express.js', 'Node.js (API)', 'GitHub Actions', 'Webpack', 'Vite', 'Docker', 'MongoDB', 'Linux', 'Storybook'] },
        { label: 'Знаком', level: 2, items: ['PlayCanvas', 'Python', 'FastAPI', 'React.js', 'Browser extensions'] },
        { label: 'Есть опыт', level: 1, items: ['Yii2', 'PHP', 'MySQL'] },
      ],
      levelLabels: { 4: 'Отлично', 3: 'Хорошо', 2: 'Есть опыт', 1: 'Знаком' },
    },
    projects: [
      { name: 'Mobistore', url: 'mobistore.uz', team: 'Фулстек', tags: ['Nuxt', 'Node.js', 'Express.js', 'PostgreSQL'], desc: 'Full-stack сайт объявлений по продаже смартфонов — листинги, поиск и панель продавца. Делал и фронтенд, и бэкенд.' },
      { name: 'GTS', url: 'globaltravel.space', team: 'Фронт + бэк', tags: ['Vue', 'Node.js'], desc: 'Сделал и фронтенд, и бэкенд платформы.' },
      { name: 'SidusHeroes', url: 'play.sidusheroes.com', team: 'Команда 10', tags: ['Vue 3', 'web3.js', 'PlayCanvas'], desc: 'Стикер-чат, полноценный чат организаций, банк и другое. Перенёс компоненты в общую npm-библиотеку.' },
      { name: 'Platinum / Spaceswap', url: 'spaceswap.app', team: 'Команда 5', tags: ['Vue 3', 'Node.js', 'Blockchain'], desc: 'Фронтенд кросс-чейн моста и пресейла NFT. Full-stack игра 2048 и админ-панель для владельцев токенов.' },
      { name: 'Vesting', url: 'vesting.cheesus.ai', team: 'Соло фронтенд', tags: ['Vue 3', 'web3.js'], desc: 'Весь фронтенд для клейма и стейкинга токенов CHEESUS.' },
      { name: 'ShippedPro', url: 'shippedpro.com', team: 'Соло фронтенд', tags: ['Vue', 'Nuxt'], desc: 'Полностью разработал фронтенд продукта.' },
      { name: 'Linda', url: 'join-linda.com', team: 'Фриланс', tags: ['Vue', 'Chrome ext'], desc: 'Фронтенд платформы и сопутствующее расширение для Chrome.' },
      { name: 'Onesix', url: 'onesix.tech', team: 'Фриланс', tags: ['Vue', 'Nuxt'], desc: 'Фронтенд продукта.' },
      { name: 'Limecoin', url: '@OfficialLimeCoinBot', team: 'Фриланс', tags: ['Telegram WebApp'], desc: 'Проект Telegram web application.' },
      { name: 'EasyBooking', url: 'easybooking.uz', team: '2 фронта', tags: ['Yii2', 'PHP', 'Vue'], desc: 'Фронтенд в команде из двух человек; полностью вёл бэкенд на Yii2 (PHP).' },
    ],
    code: [
      { name: 'Contact manager', stack: 'Vue 3', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Mini personal account', stack: 'Nuxt 3', note: 'login: admin · pass: admin', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Ball game', stack: 'Vue 3 + PlayCanvas', links: [['GitHub', '#']] },
    ],
    education: { school: 'Ташкентский университет информационных технологий', period: 'Сен 2011 — Июл 2015', degree: 'Бакалавр с отличием', grade: '87 / 100' },
    languages: [
      { name: 'Узбекский', level: 'Родной', pct: 100 },
      { name: 'Русский', level: 'Свободно', pct: 85 },
      { name: 'Английский', level: 'Средний', pct: 60 },
    ],
    contact: {
      phone: '+998 97 575 51 51',
      emails: ['boss.mamatov@gmail.com', 'mamatov.umar@mail.ru'],
      telegram: '@Mamatov_Umar',
      telegramUrl: 'https://t.me/Mamatov_Umar',
      linkedin: 'in/umar-mamatov',
      linkedinUrl: 'https://www.linkedin.com/in/umar-mamatov-4a3a27219/',
      labels: { phone: 'Телефон', email: 'Почта', telegram: 'Telegram', linkedin: 'LinkedIn' },
    },
    footer: 'Дизайн и разработка — Умар Маматов',
  },

  uz: {
    nav: { about: 'Men haqimda', experience: 'Tajriba', skills: 'Ko‘nikmalar', projects: 'Loyihalar', education: 'Ta’lim', contact: 'Aloqa' },
    hero: {
      kicker: 'Frontend arxitektori · Jamoa yetakchisi',
      name: 'Umar Mamatov',
      roleLines: ['Frontend arxitekturasi', 'Mikrofrontendlar', 'UI kutubxonalari'],
      summary:
        'Frontend jamoasiga rahbarlik qilaman va funksional ishlab chiqishda shaxsan ishtirok etaman. Masshtablanuvchi arxitekturalar, mikrofrontendlar va korporativ UI kutubxonalarini yarataman. Node.js asosidagi kuchli full-stack tajriba hamda yuqori yuklamali tizimlarni optimallashtirish ko‘nikmalariga egaman. Ishlab chiqish tezligini oshirish, interfeyslarni unifikatsiya qilish va mahsulotning barqaror ishlashini ta’minlashga ixtisoslashganman.',
      cta: 'Bog‘lanish',
      cta2: 'Tajribani ko‘rish',
      locationLabel: 'Joylashuv',
      location: 'Toshkent, O‘zbekiston',
      availLabel: 'Ochiqman',
      avail: 'Jamoa yetakchisi va frontend dasturchi rollariga',
      chips: ['Vue 3', 'Nuxt 3', 'TypeScript', 'Mikrofrontendlar', 'Web3.js', 'Node.js'],
      status: { a: '8+ yil tajriba', b: 'jamoa yetakchisi · kodda', c: 'Toshkent · GMT+5', d: 'lid va dasturchi rollariga ochiq' },
    },
    stats: [
      { value: '8+', label: 'Yil veb-dasturlashda' },
      { value: '50+', label: 'Prodakshendagi mahsulotlar' },
      { value: '6', label: 'Rahbarlik qilgan muhandislar' },
      { value: '3', label: 'Mikrofrontend arxitekturalari' },
    ],
    sectionTitles: {
      experience: 'Tajriba',
      experienceSub: 'Fintex, geymdev, blokcheyn va yuqori yuklamali SaaS bo‘ylab o‘n yillik tajriba',
      skills: 'Ko‘nikmalar',
      skillsSub: 'Qayerda kuchliman va nimani rivojlantiryapman',
      projects: 'Loyihalar',
      projectsSub: 'Men yaratgan yoki boshqargan tanlangan mahsulotlar',
      code: 'Kod namunalari',
      education: 'Ta’lim',
      languages: 'Tillar',
      contact: 'Keling, birga ishlaymiz',
      contactSub: 'Ham jamoa yetakchisi, ham frontend dasturchi takliflariga ochiqman. Eng tez Telegram orqali javob beraman.',
    },
    experience: [
      {
        company: 'Rahmat Tech', role: 'Frontend muhandislar jamoasi yetakchisi',
        period: 'Yan 2026 — hozirgacha', current: true,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Mikrofrontendlar', 'Storybook'],
        points: [
          'Frontend jamoasiga rahbarlik qilaman — arxitektura, loyihalash va ishlab chiqish standartlari.',
          'Faol dasturchi bo‘lib qolaman — funksiya va modullarni jamoa bilan birga yozaman, faqat review qilmayman.',
        ],
      },
      {
        company: 'Easy Technologies', role: 'Frontend muhandislar jamoasi yetakchisi',
        period: 'Mar 2024 — Yan 2026', current: false,
        stack: ['JavaScript', 'Vue 3', 'TypeScript', 'Nuxt.js', 'Docker', 'Mikrofrontendlar', 'FastAPI'],
        points: [
          'Mikrofrontend yondashuviga asoslangan masshtablanadigan frontend arxitekturasini loyihalashtirdim va joriy qildim.',
          'Barcha loyihalarda birxillik va qayta foydalanish uchun korporativ UI-komponentlar kutubxonasini (npm-paket) yaratdim va qo‘llab-quvvatladim.',
          'Yuqori yuklamali ilovalar unumdorligini samarali modulli integratsiya bilan optimallashtirdim.',
          'Front va bek o‘rtasidagi uzluksiz integratsiya uchun FastAPI va Node.js’da REST API ishlab chiqdim.',
        ],
      },
      {
        company: 'Sidus Games + Platinum', role: 'Frontend muhandis',
        period: 'Noy 2022 — Noy 2023', current: false,
        stack: ['JavaScript', 'Vue 3', 'Blockchain', 'web3.js', 'TypeScript', 'PlayCanvas'],
        points: [
          '«Bank» modulini va «Tashkilotlar» moduli uchun chatni yaratdim.',
          'PlayCanvas’dagi Asterally o‘yini ishlab chiqilishida qatnashdim.',
        ],
      },
      {
        company: 'Platinum', role: 'Frontend muhandis · Fintex / Kripto-startap',
        period: 'Avg 2021 — Noy 2023', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue 3', 'Nuxt.js', 'Express.js', 'Docker', 'MongoDB', 'web3.js', 'TypeScript'],
        points: [
          'Storybook kutubxonasi uchun Vue-komponentlar yozdim.',
          'Spaceswap loyihasi uchun admin-panel, 2048 o‘yini, blokcheyn ko‘prik va boshqa funksiyalarni qildim.',
          'CHEESUS tokenlarini klaym va steyking qilish uchun frontendni to‘liq ishlab chiqdim.',
        ],
      },
      {
        company: 'Elastoo Products', role: 'Frontend muhandis · Fintex / Kripto-startap',
        period: 'Noy 2020 — Avg 2021', current: false,
        stack: ['JavaScript', 'Node.js', 'Vue.js', 'Nuxt.js', 'web3.js'],
        points: ['Kompaniya mahsulotlari uchun frontend ishlab chiqish.'],
      },
      {
        company: 'Frilans', role: 'Full-Stack dasturchi',
        period: 'Dek 2017 — Noy 2020', current: false,
        stack: ['JavaScript', 'Vue.js', 'PHP', 'Nuxt.js', 'Yii2', 'MySQL'],
        points: ['Turli mijozlar uchun full-stack veb-loyihalarni «kalitli» holatda yetkazib berdim.'],
      },
    ],
    skills: {
      groups: [
        { label: 'A’lo', level: 4, items: ['JavaScript', 'TypeScript', 'Vue.js', 'Nuxt.js', 'Web3.js', 'Telegram Web App', 'HTML', 'CSS / SCSS', 'Mikrofrontendlar'] },
        { label: 'Yaxshi', level: 3, items: ['Express.js', 'Node.js (API)', 'GitHub Actions', 'Webpack', 'Vite', 'Docker', 'MongoDB', 'Linux', 'Storybook'] },
        { label: 'Tanish', level: 2, items: ['PlayCanvas', 'Python', 'FastAPI', 'React.js', 'Browser extensions'] },
        { label: 'Tajriba bor', level: 1, items: ['Yii2', 'PHP', 'MySQL'] },
      ],
      levelLabels: { 4: 'A’lo', 3: 'Yaxshi', 2: 'Tajriba bor', 1: 'Tanish' },
    },
    projects: [
      { name: 'Mobistore', url: 'mobistore.uz', team: 'Fulstek', tags: ['Nuxt', 'Node.js', 'Express.js', 'PostgreSQL'], desc: 'Smartfon oldi-sotdisi uchun full-stack e’lonlar platformasi — e’lonlar, qidiruv va sotuvchi paneli. Ham frontend, ham bekendni qildim.' },
      { name: 'GTS', url: 'globaltravel.space', team: 'Front + bek', tags: ['Vue', 'Node.js'], desc: 'Platformaning ham frontend, ham bekendini qildim.' },
      { name: 'SidusHeroes', url: 'play.sidusheroes.com', team: 'Jamoa 10', tags: ['Vue 3', 'web3.js', 'PlayCanvas'], desc: 'Stiker-chat, to‘liq tashkilotlar chati, bank va boshqalar. Komponentlarni umumiy npm-kutubxonaga ko‘chirdim.' },
      { name: 'Platinum / Spaceswap', url: 'spaceswap.app', team: 'Jamoa 5', tags: ['Vue 3', 'Node.js', 'Blockchain'], desc: 'Kross-cheyn ko‘prik va NFT presale frontendi. Full-stack 2048 o‘yini va token egalari uchun admin-panel.' },
      { name: 'Vesting', url: 'vesting.cheesus.ai', team: 'Yakka frontend', tags: ['Vue 3', 'web3.js'], desc: 'CHEESUS tokenlarini klaym va steyking qilish uchun butun frontend.' },
      { name: 'ShippedPro', url: 'shippedpro.com', team: 'Yakka frontend', tags: ['Vue', 'Nuxt'], desc: 'Mahsulotning butun frontendini ishlab chiqdim.' },
      { name: 'Linda', url: 'join-linda.com', team: 'Frilans', tags: ['Vue', 'Chrome ext'], desc: 'Platforma frontendi va unga hamroh Chrome kengaytmasi.' },
      { name: 'Onesix', url: 'onesix.tech', team: 'Frilans', tags: ['Vue', 'Nuxt'], desc: 'Mahsulot frontendi.' },
      { name: 'Limecoin', url: '@OfficialLimeCoinBot', team: 'Frilans', tags: ['Telegram WebApp'], desc: 'Telegram web application loyihasi.' },
      { name: 'EasyBooking', url: 'easybooking.uz', team: '2 frontend', tags: ['Yii2', 'PHP', 'Vue'], desc: 'Ikki kishilik jamoada frontend; Yii2 (PHP) bekendni to‘liq olib bordim.' },
    ],
    code: [
      { name: 'Contact manager', stack: 'Vue 3', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Mini personal account', stack: 'Nuxt 3', note: 'login: admin · pass: admin', links: [['GitHub', '#'], ['Demo', '#']] },
      { name: 'Ball game', stack: 'Vue 3 + PlayCanvas', links: [['GitHub', '#']] },
    ],
    education: { school: 'Toshkent axborot texnologiyalari universiteti', period: 'Sen 2011 — Iyul 2015', degree: 'Bakalavr, imtiyozli diplom', grade: '87 / 100' },
    languages: [
      { name: 'O‘zbek', level: 'Ona tili', pct: 100 },
      { name: 'Rus', level: 'Yuqori', pct: 85 },
      { name: 'Ingliz', level: 'O‘rta', pct: 60 },
    ],
    contact: {
      phone: '+998 97 575 51 51',
      emails: ['boss.mamatov@gmail.com', 'mamatov.umar@mail.ru'],
      telegram: '@Mamatov_Umar',
      telegramUrl: 'https://t.me/Mamatov_Umar',
      linkedin: 'in/umar-mamatov',
      linkedinUrl: 'https://www.linkedin.com/in/umar-mamatov-4a3a27219/',
      labels: { phone: 'Telefon', email: 'Pochta', telegram: 'Telegram', linkedin: 'LinkedIn' },
    },
    footer: 'Dizayn va ishlab chiqish — Umar Mamatov',
  },
}
